import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { BsArrowRight } from 'react-icons/bs';
import { Palette } from 'core/config';
import { Modal } from 'core/modal';
import { CardsModal, MoneyModal } from './modals';
import { createHubspotDeal } from 'core/api';

enum ApplicantStatus 
{
    NEW = 'n',
    ORIGINATED = 'o',
    REVIEW = 'r',
    CREDIT_CHECK = 'c'
}

enum ApplicationStatus 
{
    NEW = 'n',
    PRE_APPROVED = 'p',
    UNDERWRITING = 'u',
    REVIEW = 'r'
}

interface Props {
    id: string;
    name: string;
    info: string;
    cta: string;
    colour: string;
    bgColour: string;
    isLarge?: boolean;
    buttonId: string;
    userDetails: {
        email: string;
        waitlist: number;
        applicantStatus: string;
        applicationStatus: string;
        hasConsumerReport: boolean;
    };
    setTypeformId: React.Dispatch<React.SetStateAction<string>>;
}

enum Product {
    homes = 'homes',
    cards = 'cards',
    money = 'money',
    wheels = 'wheel',
    realty = 'realty',
    wills = 'wills'
}

const modals = {
    [Product.cards.toString()]: {
        Component: CardsModal,
        onRedirect: async () => {
            window.open('https://partners.koho.ca/manzil/', '_blank');
            await createHubspotDeal({ dealname: 'Started (Cards)' });
        }
    },
    [Product.money.toString()]: {
        Component: MoneyModal,
        onRedirect: async () => {
            const cid = localStorage.getItem('cid');
            window.open('https://www.manzil.ca/invest-info', '_blank');
            await createHubspotDeal({ dealname: 'Started (Money)' });
        }
    }
};

export const Tile: React.FunctionComponent<Props> = ({
    id,
    name,
    info,
    cta,
    colour,
    bgColour,
    buttonId,
    userDetails,
    isLarge,
    setTypeformId
}) => {
    const history = useHistory();
    const [ showModal, setShowModal ] = useState(false);
    const { waitlist, applicationStatus, applicantStatus, hasConsumerReport } = userDetails;
    const isHomesTile = id === Product.homes;
    const isOnWaitlist = isHomesTile
        && waitlist !== null 
        && waitlist !== undefined
        && waitlist >= 0;
    const canBeginUnderwriting = applicationStatus === ApplicationStatus.PRE_APPROVED;
    const applicationUnderReview = applicantStatus === ApplicantStatus.REVIEW;
    const canEditUnderwriting = applicationStatus === ApplicationStatus.UNDERWRITING;
    const canPullCreditCheck = applicantStatus === ApplicantStatus.CREDIT_CHECK && !hasConsumerReport;

    let onClick: (id: string) => void;

    switch (id) {
    case Product.wills:
        onClick = () => {
            window.open('https://app.muslimwill.ca/customers/signup', '_blank');
        };
        break;
    case Product.homes:
        onClick = async () => {
            if(canPullCreditCheck) {
                history.push('/credit-pull');
            } else if (isOnWaitlist && canBeginUnderwriting || canEditUnderwriting) {
                history.push('/underwriting');
            } else {
                history.push('/application');
                await createHubspotDeal({ dealname: 'Started (Homes)' });
            }
        };
        break;
    case Product.wheels:
        onClick = async () => {
            setTypeformId(id);
            await createHubspotDeal({ dealname: 'Started (Wheels)' });
        };
        break;
    case Product.realty:
        onClick = async (id: string) => {
            setTypeformId(id);
            await createHubspotDeal({ dealname: 'Started (Realty)' });
        };
        break;
    case Product.cards:
    case Product.money:
        onClick = async () => setShowModal(s => {
            const root = document.getElementById('root');
            s ? root?.classList.remove('no-overflow') : root?.classList.add('no-overflow');

            return !s;
        });
        break;
    }

    const isTileClickable = isHomesTile 
        ? !isOnWaitlist || (canBeginUnderwriting && !applicationUnderReview) || canPullCreditCheck || canEditUnderwriting
        : true;

    return (
        <>
            <TileContainer 
                isEnabled={isTileClickable}
                onClick={isTileClickable ? () => onClick(id) : null} 
                bgColour={bgColour} 
                colour={colour} 
                isLarge={isLarge}
            >
                <TileName isLarge={isLarge}>Halal {name}</TileName>
                {isOnWaitlist && !canPullCreditCheck ? (
                    canBeginUnderwriting ? (
                        <TileInfo isLarge={isLarge}>Begin the underwriting process</TileInfo>
                    ) : canEditUnderwriting ? (
                        <TileInfo>
                            Edit uploaded documents
                        </TileInfo>
                    ) : (
                        <>
                            <TileInfo>Waitlist position:</TileInfo>
                            <WaitlistPosition>{waitlist}</WaitlistPosition>
                        </>
                    )
                ) : (
                    <TileInfo textOverflow={id === Product.cards} isLarge={isLarge}>{canPullCreditCheck && isHomesTile ? 'Click to authorize your credit pull' : info}</TileInfo>
                )}
                {(!isHomesTile) && (
                    <TileButton id={buttonId} isLarge={isLarge}>
                        <ButtonText isLarge={isLarge}>{cta}</ButtonText>
                        <RightArrow large={isLarge ? 'large' : ''} />
                    </TileButton>
                )}
                {(isHomesTile && isTileClickable) && (
                    <TileButton id={buttonId} isLarge={isLarge}>
                        {
                            canBeginUnderwriting ? (
                                <ButtonText isLarge={isLarge}>Start</ButtonText>
                            ) : canPullCreditCheck ? (
                                <ButtonText isLarge={isLarge}>Authorize Credit Check</ButtonText>
                            ) : canEditUnderwriting ? (
                                <ButtonText isLarge={isLarge}>Edit</ButtonText>
                            ) : (
                                <ButtonText isLarge={isLarge}>{cta}</ButtonText>
                            )
                        }
                        <RightArrow large={isLarge ? 'large' : ''} />
                    </TileButton>
                )}
            </TileContainer>
            {showModal && (<Modal setOpen={() => onClick('')} {...modals[id]} />)}
        </>
    );
};

const TileContainer = styled.div<{ isEnabled: boolean; bgColour: string; colour: string; isLarge?: boolean; onClick: any }>`
    border-radius: 23px;
    width: 191px;
    height: 191px;
    box-shadow: 3px 8px 7px #5A4D3940;
    flex-direction: column;
    margin-right: 30px;
    margin-bottom: 30px;
    position: relative;
    ${({ isEnabled }) => isEnabled && 'cursor: pointer;'}

    // @media only screen and (max-width: 600px) {
    //     border-radius: 13px;
    //     // width: calc(100% - 30px);
    //     // height: 93px;
    //     margin-right: 25px;
    //     margin-bottom: 25px;
    // }

    ${({ bgColour, colour, isLarge }) => (
        `
        background-color: ${bgColour};
        color: ${colour};
        p {
            color: ${colour};
        }

        ${isLarge ? `
            width: 410px;
            height: 365px;
            padding-top: 20px;
            padding-left: 20px;

            @media only screen and (max-width: 1330px) {
                width: 315px;
                height: 313px;
            }

            @media only screen and (max-width: 900px) {
                width: calc(100% - 20px);
                height: 144px;
            }
        ` : `
            @media only screen and (max-width: 1329px) {
                width: 145px;
                height: 144px;
                margin-right: 46px;
                margin-bottom: 46px;
            }
        `}
    `
    )}
`;

const TileName = styled.p<{ isLarge?: boolean; }>`
    text-align: left;
    font: normal normal bold 19px 'SofiaPro-Regular';
    line-height: 1;
    padding-left: 22px;
    padding-top: 5px;

    // @media only screen and (max-width: 600px) {
    //     font-size: 15px;
    //     padding-left: 14px;
    // }

    ${({ isLarge }) => isLarge ? `
        font-size: 28px;
        margin-bottom: 20px;
        @media only screen and (max-width: 1329px) {
            margin-top: 10px;
            margin-left: -5px;
        }
    ` : `
        @media only screen and (max-width: 1329px) {
            font-size: 15px;
            margin-top: 10px;
            margin-left: -5px;
        }
    `}
`;

const TileInfo = styled.p<{ textOverflow?: boolean; isLarge?: boolean; }>`
    text-align: left;
    font: normal normal normal 12px 'SofiaPro-Regular';
    color: ${Palette.BLUE};
    line-height: ${({ isLarge }) => isLarge ? '20px' : '14px'};
    padding-left: 22px;
    padding-right: ${({ isLarge }) => isLarge ? '80px' : '22px'};;

    // @media only screen and (max-width: 600px) {
    //     font-size: 11px;
    //     padding-left: 14px;
    //     width: 195px;
    //     ${({ textOverflow }) => textOverflow && 'white-space: nowrap;'}
    // }

    ${({ isLarge }) => isLarge ? `
        font-size: 16px;
        @media only screen and (max-width: 1329px) {
            margin-top: -10px;
            margin-left: -5px;
        }

        @media only screen and (max-width: 1100px) {
            font-size: 14px;
        }
    ` : `
        @media only screen and (max-width: 1329px) {
            font-size: 11px;
            margin-top: -10px;
            margin-left: -5px;
        }
    `}
`;

const WaitlistPosition = styled.div`
    font: normal normal normal 52px 'SofiaPro-Bold';
    text-align: center;
`;

const TileButton = styled.button<{ isLarge?: boolean; }>`
    display: flex;
    position: absolute;
    justify-content: space-between;
    align-items: center;
    background-color: ${Palette.ORANGE};
    border-radius: 5px;
    box-shadow: none;
    border: none;
    width: 97px;
    height: 26px;
    margin-right: 22px;
    margin-bottom: 21px;
    cursor: pointer;
    bottom: 0;
    right: 0;

    :hover {
        background-color: ${Palette.LIGHT_ORANGE};
    }

    :focus {
        outline: 0;
    }

    @media only screen and (max-width: 600px) {
        margin-right: 12px;
        margin-bottom: 12px;
        // background-color: transparent;

        :hover {
            // background-color: transparent;
        }
    }

    ${({ isLarge }) => isLarge ? `
        width: 166px;
        height: 41px;
        padding-right: 15px;
        margin-right: 42px;
        margin-bottom: 44px;

        @media only screen and (max-width: 900px) {
            width: 36px;
            height: 36px;
            border-radius: 100%;
            margin-right: 12px;
            margin-bottom: 12px;
        }
    ` : `
        @media only screen and (max-width: 1329px) {
            width: 36px;
            height: 36px;
            border-radius: 100%;
            margin-right: 12px;
            margin-bottom: 12px;
        }

        @media only screen and (min-width: 1330px) {
            width: 125px;
        }
    `}
`;

const ButtonText = styled.span<{ isLarge?: boolean; }>`
    padding-left: 13px;
    font: normal normal normal 7px 'SofiaPro-Regular';
    text-transform: uppercase;
    text-decoration: none;
    color: ${Palette.EGGSHELL_WHITE};
    width: 125px;
    margin-left: -20px;
    left: 0;

    @media only screen and (max-width: 600px) {
        padding-left: 0;
        font-size: 0;
        margin-left: 1px;
        width: 0;
    }

    @media only screen and (min-width: 1300px) {
        font-size: 10px;
    }

    ${({ isLarge }) => isLarge ? `
        font-size: 11px !important;
        margin-left: -5px;
        @media only screen and (max-width: 900px) {
            display: none;
        }
    ` : `
        @media only screen and (max-width: 1329px) {
            display: none;
        }
    `}
`;

const RightArrow = styled(BsArrowRight) <{ isHalalCardsTile?: boolean; large: string; }>`
    color: ${Palette.EGGSHELL_WHITE};
    @media only screen and (max-width: 600px) {
        width: 20px;
        height: 20px;

        color: ${({ isHalalCardsTile }) => {
        return isHalalCardsTile ? Palette.BLUE : Palette.EGGSHELL_WHITE;
    }};
    }

    @media only screen and (max-width: 1329px)
    {
        ${({ large }) => !large && 'width: 100px;'}
    }

    @media only screen and (max-width: 900px)
    {
        ${({ large }) => large && 'position: absolute; padding-left: 3px;'}
    }
`;
